import React from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DashProfitLossSidebarSkeleton } from "./DashCardSkeletons";
import { getAmount } from "../../../../utils/helper";

function MetricTooltip({ label, sectionName, netTaxTooltip }) {
  if (!label) return null;

  const cleanLabel = label.trim().toLowerCase();
  const cleanSection = sectionName?.trim().toLowerCase() || "";
  if (cleanSection.includes("refund") || cleanSection.includes("return")) {
    return null;
  }

  if (cleanLabel === "net tax" || cleanLabel === "net tax (gst)") {
    if (!netTaxTooltip) return null;
    return (
      <Tooltip title={netTaxTooltip} placement="top">
        <span
          className="ms-2 text-muted"
          style={{
            cursor: "help",
            display: "inline-block",
            transform: "none",
            lineHeight: 1,
          }}
        >
          <InfoCircleOutlined
            style={{
              transform: "none",
              display: "inline-block",
              fontSize: "0.75rem",
              verticalAlign: "middle",
            }}
          />
        </span>
      </Tooltip>
    );
  }

  const METRIC_TOOLTIPS_CLEANED = {
    "fba per unit fulfillment fee": "Flat fee Amazon charges per unit to pick, pack, and ship your FBA orders to customers.",
    "fba weight based fee": "Fulfillment charge based on the item's size and weight; heavier or larger items cost more to ship.",
    "commission": "Amazon's referral fee, taken as a percentage of each sale based on the product category.",
    "fixed closing fee": "A flat per-item fee Amazon applies on certain categories (often media and lower-priced items) in addition to the referral fee.",
    "item tds": "Tax Deducted at Source on the item value, withheld by Amazon and deposited with tax authorities on your behalf.",
    "marketplace withheld tax": "Tax Amazon withstands from your payout as the marketplace facilitator and remits to the government on your behalf.",
    "fba storage fee": "Monthly charge for storing your inventory in Amazon's fulfillment centers, based on the space (volume) your stock occupies.",
    "inbound transportation": "Cost of shipping your inventory into Amazon's fulfillment centers using Amazon's partnered carrier program.",
    "fba removal": "Fee for removing or disposing of inventory from Amazon's fulfillment centers and returning it to you.",
    "easy ship fee": "Charge for Amazon's Easy Ship service, where you store and pack the order but Amazon handles pickup and delivery.",
    "reversal reimbursement": "A credit back to you (positive amount) when Amazon reverses a previously charged fee or reimburses you for a lost or damaged item.",
    "free replacement refund items": "Credit/adjustment related to items sent as free replacements to customers, reimbursed back to your account.",
    "postage refund": "Shipping charges refunded back to you, typically tied to returns or order cancellations.",
    "i gst": "Integrated GST applied on interstate transactions or fees, collected and remitted through Amazon.",
    "promotional rebates": "Deductions for discounts, coupons, and promotions you funded, reducing your net payout.",
    "refund cost": "Net cost to you when customers return orders, including the refunded amount minus any fees Amazon returns to you.",
    "cost of goods sold": "COGS = (units sold − refunds) × unit cost + unsellable returns charged at (100% − recovery %) set in the Products tab.",
  };

  const tooltipText = METRIC_TOOLTIPS_CLEANED[cleanLabel];
  if (!tooltipText) return null;

  return (
    <Tooltip title={tooltipText} placement="top">
      <span
        className="ms-2 text-muted"
        style={{
          cursor: "help",
          display: "inline-block",
          transform: "none",
          lineHeight: 1,
        }}
      >
        <InfoCircleOutlined
          style={{
            transform: "none",
            display: "inline-block",
            fontSize: "0.75rem", // matches fs-7
            verticalAlign: "middle",
          }}
        />
      </span>
    </Tooltip>
  );
}


function formatCompactAmount(value, currencySign) {
  const amount = Number(value) || 0;
  const sign = amount < 0 ? "-" : "";
  return getAmount(Math.abs(amount), currencySign, { decimals: 2 });
}

function formatPlainNumber(value) {
  const n = Number(value) || 0;
  const sign = n < 0 ? "-" : "";
  return `${sign}${Math.abs(Math.round(n)).toLocaleString()}`;
}

function formatRowAmount(row, currencySign) {
  if (row.amountFormat === "number") return formatPlainNumber(row.amount);
  return getAmount(Number(row.amount) || 0, currencySign, { decimals: 2 });
}

function formatChildValue(child, currencySign) {
  if (child.type === "number") return formatPlainNumber(child.value);
  return getAmount(Number(child.value) || 0, currencySign, { decimals: 2 });
}

function formatTrendPct(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return null;
  const n = Number(pct);
  const arrow = n > 0 ? "↑" : n < 0 ? "↓" : "→";
  return `${arrow}${Math.abs(n).toFixed(2)}%`;
}

function CompareLine({ compare, currencySign, compact = false, amountFormat = "number" }) {
  if (!compare) return null;
  const pct = formatTrendPct(compare.pct);
  const deltaText =
    amountFormat === "number"
      ? formatPlainNumber(compare.delta)
      : getAmount(compare.delta, currencySign, { decimals: 2 });
  
  const isPctPositive = compare.pct > 0;

  return (
    <div className="pl-sidebar__compare">
      <span >
        {deltaText}
      </span>
      {pct ? (
        <span
          className={`pl-sidebar__compare-pct ${isPctPositive ? "pl-sidebar__compare-pct--up" : "pl-sidebar__compare-pct--down"}`}
        >
          {pct}
        </span>
      ) : null}
    </div>
  );
}

function PlSidebarChildRow({ child, currencySign, sectionName, netTaxTooltip, nested = false }) {
  return (
    <div className={`pl-sidebar__child ${nested ? "pl-sidebar__child--nested" : ""}`}>
      <span className="pl-sidebar__child-title">
        {child.title}
        <MetricTooltip label={child.title} sectionName={sectionName} netTaxTooltip={netTaxTooltip} />
      </span>
      <span className="pl-sidebar__child-value">{formatChildValue(child, currencySign)}</span>
    </div>
  );
}

function PlSidebarChildList({ items, currencySign, sectionName, netTaxTooltip }) {
  return items.map((child) => (
    <div key={child.key} className="pl-sidebar__child-block">
      <PlSidebarChildRow
        child={child}
        currencySign={currencySign}
        sectionName={sectionName}
        netTaxTooltip={netTaxTooltip}
      />
      {child.children?.length ? (
        <div className="pl-sidebar__child-nested">
          {child.children.map((nestedChild, index) => (
            <div
              key={nestedChild.key}
              className={`pl-sidebar__child-nested-row ${
                index === child.children.length - 1 ? "pl-sidebar__child-nested-row--last" : ""
              }`}
            >
              <span className="pl-sidebar__child-connector" aria-hidden />
              <PlSidebarChildRow
                child={nestedChild}
                currencySign={currencySign}
                sectionName={sectionName}
                netTaxTooltip={netTaxTooltip}
                nested
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  ));
}

function RowAmounts({ row, currencySign }) {
  return (
    <div className="pl-sidebar__row-amounts">
      <div className="pl-sidebar__amount" >
        {formatRowAmount(row, currencySign)}
      </div>
      {row.kind === "gross" && row.margin != null ? (
        <div className="pl-sidebar__margin">{Number(row.margin).toFixed(2)}% margin</div>
      ) : (
        <CompareLine compare={row.compare} currencySign={currencySign} compact amountFormat={row.amountFormat} />
      )}
    </div>
  );
}

const TAX_CREDIT_TOOLTIP_RECOVERABLE =
  "Tax paid exceeded tax collected - the surplus is credited to profit, so Net Profit is higher than Gross Profit.";

export default function DashProfitLossSidebarCard({
  loading,
  profitLoss,
  currencySign = "₹",
  netTaxTooltip,
  isTaxInputCreditRecoverable = false,
}) {
  const rows = profitLoss?.rows || [];
  const net = profitLoss?.netProfit || {};
  const isNetPositive = Number(net.amount) > 0;
  const grossRow = rows.find((row) => row.kind === "gross");
  const grossAmount = Number(grossRow?.amount) || 0;
  const netAmount = Number(net.amount) || 0;
  const showTaxCreditBadge = netAmount > grossAmount;

  return (
    <div className="pl-sidebar card border border-gray-200 shadow-none  overflow-hidden">
      {loading ? (
        <DashProfitLossSidebarSkeleton />
      ) : (
        <>
          <div className="pl-sidebar__head">
            <div>
              <h3 className="pl-sidebar__title fw-bolder mb-0 fs-5">Profit &amp; Loss</h3>
              <div className="pl-sidebar__subtitle">Expand rows to see breakdown</div>
            </div>
          </div>

          <div className="pl-sidebar__scroll">
            <div className={`pl-sidebar__rows${showTaxCreditBadge ? " pl-sidebar__rows--has-tax-credit" : ""}`}>
              {rows.map((row) => {
                const isCogs = row.kind?.toLowerCase() === "cogs" || row.label?.toLowerCase().includes("cost of goods");
                const expandable = !!(row.children && row.children.length) && !isCogs;

                if (row.kind === "gross") {
                  const grossAmount = Number(row.amount) || 0;
                  const isGrossPositive = grossAmount > 0;
                  return (
                    <div
                      key={row.key}
                      className={`pl-sidebar__row pl-sidebar__row--gross ${
                        isGrossPositive ? "pl-sidebar__row--gross-positive" : "pl-sidebar__row--gross-negative"
                      }`}
                    >
                      <div className="pl-sidebar__row-main">
                        <div className="pl-sidebar__row-title">
                          {row.label}
                          <MetricTooltip label={row.label} sectionName={row.kind} netTaxTooltip={netTaxTooltip} />
                        </div>
                        <div className="pl-sidebar__row-subtitle">{row.subtitle}</div>
                      </div>
                      <RowAmounts row={row} currencySign={currencySign} />
                    </div>
                  );
                }

                if (expandable) {
                  return (
                    <div key={row.key} className={`pl-sidebar__row pl-sidebar__row--${row.kind} pl-sidebar__row--expandable`}>
                      <details className="pl-sidebar__details">
                        <summary className="pl-sidebar__details-summary">
                          <span className="pl-sidebar__chev" aria-hidden>
                            <i className="ki-outline ki-right" style={{ fontSize: "1rem" }} />
                          </span>
                          <span className="pl-sidebar__dash" aria-hidden />
                          <div className="pl-sidebar__row-main">
                            <div className="pl-sidebar__row-title">
                              {row.label}
                              <MetricTooltip label={row.label} sectionName={row.kind} netTaxTooltip={netTaxTooltip} />
                            </div>
                            <div className="pl-sidebar__row-subtitle">{row.subtitle}</div>
                          </div>
                          <RowAmounts row={row} currencySign={currencySign} />
                        </summary>
                        <div className="pl-sidebar__children" role="region" aria-label={`${row.label} breakdown`}>
                          <PlSidebarChildList
                            items={row.children}
                            currencySign={currencySign}
                            sectionName={row.kind}
                            netTaxTooltip={netTaxTooltip}
                          />
                        </div>
                      </details>
                    </div>
                  );
                }

                if (isCogs) {
                  return (
                    <div key={row.key} className={`pl-sidebar__row pl-sidebar__row--${row.kind} pl-sidebar__row--expandable`}>
                      <div className="pl-sidebar__details-summary pl-sidebar__details-summary--static">
                        <span className="pl-sidebar__chev pl-sidebar__chev--placeholder" aria-hidden>
                          <i className="ki-outline ki-right" style={{ fontSize: "1rem" }} />
                        </span>
                        <span className="pl-sidebar__dash" aria-hidden />
                        <div className="pl-sidebar__row-main">
                          <div className="pl-sidebar__row-title">
                            {row.label}
                            <MetricTooltip label={row.label} sectionName={row.kind} netTaxTooltip={netTaxTooltip} />
                          </div>
                          <div className="pl-sidebar__row-subtitle">{row.subtitle}</div>
                        </div>
                        <RowAmounts row={row} currencySign={currencySign} />
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={row.key} className={`pl-sidebar__row pl-sidebar__row--${row.kind}`}>
                    <span className="pl-sidebar__chev" aria-hidden>›</span>
                    <span className="pl-sidebar__dash" aria-hidden />
                    <div className="pl-sidebar__row-main">
                      <div className="pl-sidebar__row-title">
                        {row.label}
                        <MetricTooltip label={row.label} sectionName={row.kind} netTaxTooltip={netTaxTooltip} />
                      </div>
                      <div className="pl-sidebar__row-subtitle">{row.subtitle}</div>
                    </div>
                    <RowAmounts row={row} currencySign={currencySign} />
                  </div>
                );
              })}
            </div>

            <div className="pl-sidebar__footer">
              {showTaxCreditBadge && isTaxInputCreditRecoverable ? (
                <div className="pl-sidebar__tax-credit-strip" role="note">
                  <span className="pl-sidebar__tax-credit-label">Tax credit applied</span>
                  <Tooltip title={TAX_CREDIT_TOOLTIP_RECOVERABLE} placement="top">
                    <span className="pl-sidebar__tax-credit-info" aria-label="Tax credit info">
                      <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                </div>
              ) : null}

              <div className={`pl-sidebar__net ${
                  isNetPositive ? "pl-sidebar__net--positive" : "pl-sidebar__net--negative"
                }`}>
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <div>
                    <div className="pl-sidebar__net-label">= {net.label || "Net Profit"}</div>
                    <div className="pl-sidebar__net-subtitle">Your take-home</div>
                  </div>
                  <div className="text-end">
                    <div className="pl-sidebar__net-amount">
                      {getAmount(Number(net.amount) || 0, currencySign, { decimals: 2 })}
                    </div>
                    <div className="pl-sidebar__net-margin">
                      {Number(net.margin || 0).toFixed(2)}% margin · {getAmount(Number(net.payout) || 0, currencySign, { decimals: 2 })}
                    </div>
                  </div>
                </div>
                {net.compare ? (
                  <div className="pl-sidebar__net-compare">
                    <span>{profitLoss?.compareLabel || "vs prior period"}</span>
                    <span >
                      {getAmount(Number(net.compare.delta) || 0, currencySign, { decimals: 2 })}
                    </span>
                    {formatTrendPct(net.compare.pct) ? (
                      <span
                        className={`pl-sidebar__compare-pct ${
                          Number(net.compare.pct) > 0
                            ? "pl-sidebar__compare-pct--up"
                            : "pl-sidebar__compare-pct--down"
                        }`}
                      >
                        {formatTrendPct(net.compare.pct)}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}